/* eslint-disable no-nested-ternary */
/* eslint-disable no-extend-native */
import Vue from 'vue'

import { FOREMAN_FILTER_STATUSES } from '@/constants/statuses'
import { fetchDivisions } from '@/services/divisionsService'
import { fetchOrders } from '@/services/orderService'
import { fetchStatusesCount } from '@/services/orderStatusesService'
import { fetchStopListEntries } from '@/services/stopListService'
import { createBoundedTtlSet } from '@/utils/boundedTtlSet'
import { $themeConfig } from '@themeConfig'

import { ordersStore, menuStore } from '../gql/query'
import { newAbortSignal } from '../utils/utils'

import { isValidInvoicePrintEnvelope, PRINT_EVENT_TYPES } from './validators'

const { GET_COURIERS } = ordersStore()

const { GET_MENU_BY_NAME } = menuStore()

export const STATUSES_FOR_FILTER = [
    'cooking',
    'send_error',
    'waiting_start_cooking',
    'completed',
    'sent_to_division',
    'delivered',
    'on_the_way',
    'canceled',
    'collected',
    'canceled_by_manager',
    'no_printed_invoice',
]

const STOP_LIST_TIMER = 15 * 1000
let globalStopListCountTimerId = null
const PROCESSED_EVENT_TTL = 10 * 60 * 1000
const PROCESSED_EVENT_LIMIT = 5000
const processedEventIds = createBoundedTtlSet({
    ttlMs: PROCESSED_EVENT_TTL,
    maxSize: PROCESSED_EVENT_LIMIT,
})
const completedCorrelations = createBoundedTtlSet({
    ttlMs: PROCESSED_EVENT_TTL,
    maxSize: PROCESSED_EVENT_LIMIT,
})
const PRINT_JOB_TIMEOUT = 7 * 60 * 1000
let printJobTimeoutId = null

const clearPrintJobTimeout = () => {
    if (!printJobTimeoutId) return
    clearTimeout(printJobTimeoutId)
    printJobTimeoutId = null
}

const handlePrintResult = (commit, envelope) => {
    const { printed_count: printedCount, unprinted_count: unprintedCount } =
        envelope.data

    commit('SET_PRINTED_INVOICES_COUNT', printedCount)
    commit('SET_UNPRINTED_INVOICES', envelope.data.unprinted)
    commit('UPDATE_MULTIPRINT_JOB', {
        status: 'completed',
        printedCount,
        unprintedCount,
        unprinted: envelope.data.unprinted,
        completedAt: Date.now(),
    })
    commit('SET_MULTIPRINT_STATUS', false)
}

const handlePrintError = (state, commit, envelope) => {
    const job = state.multiPrintJob
    const unprintedCount = job?.orderIds?.length || 0

    commit('SET_PRINTED_INVOICES_COUNT', 0)
    commit('SET_UNPRINTED_INVOICES', [
        {
            code: 'print_error',
            message: envelope.data.message,
            count: unprintedCount,
        },
    ])
    commit('UPDATE_MULTIPRINT_JOB', {
        status: 'error',
        message: envelope.data.message,
        printedCount: 0,
        unprintedCount,
        completedAt: Date.now(),
    })
    commit('SET_MULTIPRINT_STATUS', false)
}

export default {
    handleInvoicePrintEvent({ state, commit }, envelope) {
        if (!isValidInvoicePrintEnvelope(envelope)) {
            console.warn('[PrintNotifications] Пропущено некорректное событие')
            return
        }

        const now = Date.now()
        const job = state.multiPrintJob
        if (
            !job ||
            ['completed', 'error'].includes(job.status) ||
            job.correlationId !== envelope.correlation_id ||
            processedEventIds.has(envelope.id, now) ||
            completedCorrelations.has(envelope.correlation_id, now)
        ) {
            return
        }

        clearPrintJobTimeout()
        if (envelope.type === PRINT_EVENT_TYPES.RESULT) {
            handlePrintResult(commit, envelope)
        } else {
            handlePrintError(state, commit, envelope)
        }

        processedEventIds.add(envelope.id, now)
        completedCorrelations.add(envelope.correlation_id, now)
    },

    cancelInvoicePrintTimeout() {
        clearPrintJobTimeout()
    },

    startInvoicePrintTimeout({ state, commit }, correlationId) {
        clearPrintJobTimeout()
        printJobTimeoutId = setTimeout(() => {
            const job = state.multiPrintJob
            if (
                !job ||
                job.correlationId !== correlationId ||
                ['completed', 'error'].includes(job.status)
            ) {
                return
            }

            commit('SET_UNPRINTED_INVOICES', [
                {
                    code: 'print_timeout',
                    message: 'Не удалось дождаться результата печати',
                    count: job.orderIds.length,
                },
            ])
            commit('UPDATE_MULTIPRINT_JOB', {
                status: 'error',
                message: 'Не удалось дождаться результата печати',
                printedCount: 0,
                unprintedCount: job.orderIds.length,
                completedAt: Date.now(),
            })
            commit('SET_MULTIPRINT_STATUS', false)
            completedCorrelations.add(correlationId)
            printJobTimeoutId = null
        }, PRINT_JOB_TIMEOUT)
    },

    resetInvoicePrintEvents() {
        clearPrintJobTimeout()
        processedEventIds.clear()
        completedCorrelations.clear()
    },

    async fetchOrders({ commit, state, dispatch }, { limit, offset }) {
        await dispatch('fetchStatuses', {
            paramsSearch: {
                divisions: state.paramsSearch?.deps,
                delivered_date: state.paramsSearch?.delivered_date,
            },
        })
        if (!state.paramsSearch.statuses.length) {
            const params = {
                ...state.paramsSearch,
                statuses: [
                    ...STATUSES_FOR_FILTER.filter(
                        status => status !== 'no_printed_invoice'
                    ),
                ],
            }
            await commit('SET_PARAMS', params)
        }

        try {
            const ordersResponse = await fetchOrders(
                offset,
                limit,
                state.paramsSearch
            )
            const ordersData = ordersResponse.data
            commit('ADD_TO_ORDERS', ordersData.orders ?? [])
            commit('SET_COUNT', ordersData.count ?? 0)
        } catch (error) {
            console.error(error)
        }
    },

    updateOrder(ctx, { id, changes }) {
        return new Promise((resolve, reject) => {
            Vue.axios
                .put(`${$themeConfig.app.backUrl}/orders/${id}`, changes)
                .then(response => {
                    resolve(response)
                })
                .catch(error => {
                    reject(error)
                })
        })
    },
    updateCourier(ctx, { idOrder, idCurier }) {
        return new Promise((resolve, reject) => {
            Vue.axios
                .put(
                    `${$themeConfig.app.backUrl}/orders/${idOrder}/courier/${
                        idCurier || ''
                    }`
                )
                .then(response => {
                    resolve(response)
                })
                .catch(error => {
                    reject(error)
                })
        })
    },
    setCouriers({ commit, state }, val) {
        if (val) {
            const courier =
                state.couriersByRestaurant.find(e => e.id === val) || {}
            commit('SET_COURIER', courier)
        } else {
            commit('SET_COURIER', {})
        }
    },
    fetchMenu({ commit }, obj) {
        return new Promise((resolve, reject) => {
            Vue.axios
                .post($themeConfig.app.gqlUrl, {
                    query: GET_MENU_BY_NAME,
                    variables: {
                        brandId: obj.brand,
                        q: `%${obj.q}%`,
                        limit: obj.limit,
                        offset: obj.limit * obj.offset,
                        priceTypeRkId: obj.priceTypeRkId,
                    },
                })
                .then(response => {
                    if (obj.offset) {
                        commit('ADD_TO_MENU', response.data)
                    } else {
                        commit('SET_MENU', response.data)
                    }
                    resolve()
                })
                .catch(error => {
                    reject(error)
                })
        })
    },

    async fetchStatuses({ commit }, { paramsSearch }) {
        try {
            const response = await fetchStatusesCount(paramsSearch)
            const statuses = response.data ?? []
            const fullStatuses = FOREMAN_FILTER_STATUSES.map(defaultStatus => {
                const serverStatus = statuses.find(
                    s => s.id === defaultStatus.id
                )
                if (serverStatus) {
                    return {
                        ...defaultStatus,
                        ...serverStatus,
                    }
                }

                return {
                    ...defaultStatus,
                    count: 0,
                }
            })
            commit('ADD_TO_STATUSES', fullStatuses)
        } catch (error) {
            console.error('Ошибка при получении списка статусов', error)
            throw error
        }
    },

    fetchCouriers({ commit, state, dispatch }) {
        commit('CLEAR_COURIERS', [])
        const { deps } = state.paramsSearch
        deps.forEach(async element => {
            const result = await dispatch('fetchCouriersByRestaurant', {
                restaurantId: element,
            })

            if (result) {
                commit('ADD_COURIERS', result)
            } else {
                commit('CLEAR_COURIERS', [])
            }
        })

        Vue.axios
            .post(`${$themeConfig.app.gqlUrl}?refresh=true`, {
                query: GET_COURIERS,
            })
            .then(response => {
                if (response.data.data.courier) {
                    commit('ADD_COURIERS', response.data.data.courier)
                }
            })
            .catch(() => {})
    },

    fetchCouriersByRestaurant({ commit }, { restaurantId, status }) {
        return new Promise((resolve, reject) => {
            if (!restaurantId) {
                commit('SET_COURIERS', [])
                resolve()
            }
            Vue.axios
                .get(`${$themeConfig.app.courierUrl}/couriers`, {
                    params: {
                        division_id: restaurantId,
                        status: Array.isArray(status)
                            ? status.join(',')
                            : status,
                    },
                })
                .then(response => {
                    commit('SET_COURIERS', response.data?.couriers || [])
                    resolve(response.data?.couriers || [])
                })
                .catch(error => {
                    commit('SET_COURIERS', [])
                    reject(error)
                })
        })
    },

    fetchRestaurant({ commit }, restaurantId) {
        return new Promise((resolve, reject) => {
            Vue.axios
                .get(
                    `${$themeConfig.app.backUrlDivisions}/divisions/${restaurantId}`
                )
                .then(response => {
                    commit('SET_RESTAURANT', Object.assign(response.data))
                    resolve()
                })
                .catch(error => {
                    reject(error)
                })
        })
    },

    fetchOrderById({ commit }, id) {
        return new Promise((resolve, reject) => {
            Vue.axios
                .get(`${$themeConfig.app.backUrl}/orders/${id}`)
                .then(response => {
                    commit('SET_PREVIEW_ORDER', response)
                    resolve()
                })
                .catch(error => {
                    reject(error)
                })
        })
    },

    fetchOrderChanges({ commit }, { id, limit, offset }) {
        const getStr = `${$themeConfig.app.backUrl}/changes?order_id=${id}&limit=${limit}&offset=${offset}`

        return new Promise((resolve, reject) => {
            if (id) {
                Vue.axios
                    .get(getStr)
                    .then(response => {
                        commit('SET_CHANGES_ORDER', response)
                        resolve()
                    })
                    .catch(() => {
                        reject()
                    })
            } else {
                resolve()
            }
        })
    },
    printCheckEvents(ctx, { check_type_id, order_id }) {
        return new Promise((resolve, reject) => {
            Vue.axios
                .post(
                    `${$themeConfig.app.backUrl}/orders/${order_id}/print_check_events`,
                    {
                        check_type_id,
                    },
                    {
                        timeout: 2000,
                        signal: newAbortSignal(2000),
                    }
                )
                .then(response => {
                    resolve(response)
                })
                .catch(error => {
                    reject(error)
                })
        })
    },

    async fetchDivisions({ state, commit }) {
        if (state.divisions.length) return

        try {
            const response = await fetchDivisions({ mod: 'short' })
            commit('SET_DIVISIONS', response.data)
        } catch (error) {
            console.error(error)
        }
    },

    deleteEvents({ state, commit, getters }, orderId) {
        if (!orderId) return null

        const ids = getters.getIdEventsByOrderId(orderId)

        Vue.axios
            .delete(`${$themeConfig.app.backUrl}/events/${ids}`)
            .then(() => {
                commit('DELETE_NOTIFICATION', ids)
                commit('SET_SELECTED_NOTIFY_FIRST')
            })
            .catch(error => {
                console.error(error)
            })

        return state.notifications[0]
    },

    markOrderForImmediateSend(ctx, { orderId, sendText, isValidate = false }) {
        return new Promise((resolve, reject) => {
            Vue.axios
                .patch(
                    `${$themeConfig.app.backUrl}/orders/${orderId}/immediate_send`,
                    {
                        note: sendText,
                        validate: isValidate,
                    }
                )
                .then(response => {
                    if (response.data?.errors) {
                        reject(response.data.errors[0].message)
                    }
                    resolve(response.data)
                })
                .catch(error => {
                    reject(error)
                })
        })
    },
    cancelFiscalCheck(ctx, orderId) {
        return new Promise((resolve, reject) => {
            Vue.axios
                .post(
                    `${$themeConfig.app.backUrl}/orders/${orderId}/undo_receipt`
                )
                .then(response => {
                    resolve(response)
                })
                .catch(error => {
                    reject(error?.response?.data?.error || '')
                })
        })
    },

    async fetchStopListCountForBadge({ commit, dispatch }, divisionId) {
        const setBadgeCount = count =>
            commit('stopList/SET_LAST_KNOWN_STOPPED_COUNT', count, {
                root: true,
            })

        if (!divisionId) {
            setBadgeCount(0)
            return
        }
        try {
            const response = await fetchStopListEntries(divisionId, {
                active_only: true,
                limit: 1,
            })
            setBadgeCount(response?.data?.total_count || 0)
        } catch (error) {
            setBadgeCount(0)
            if (error.response?.status === 401) {
                // При 401 останавливаем поллинг, чтобы не долбить запросы
                dispatch('stopStopListBadgePolling')
            }
            console.error(
                'Ошибка загрузки счетчика стоп-листа для значка',
                error
            )
        }
    },

    startStopListBadgePolling({ dispatch }, divisionId) {
        if (globalStopListCountTimerId) {
            clearInterval(globalStopListCountTimerId)
        }
        if (!divisionId) return

        dispatch('fetchStopListCountForBadge', divisionId)

        globalStopListCountTimerId = setInterval(() => {
            dispatch('fetchStopListCountForBadge', divisionId)
        }, STOP_LIST_TIMER)
    },

    stopStopListBadgePolling({ commit }) {
        if (globalStopListCountTimerId) {
            clearInterval(globalStopListCountTimerId)
            globalStopListCountTimerId = null
        }
        // Опционально: сбрасываем счетчик при выходе из раздела
        commit('stopList/SET_LAST_KNOWN_STOPPED_COUNT', 0, { root: true })
    },
}
